/**
 * Maps a resource path segment to the parameter name that should replace
 * the next segment (the dynamic ID) when building a URL template.
 */
const SEGMENT_PARAM_MAP: Record<string, string> = {
  organisation: 'organisationId',
  organization: 'organisationId',
  branch: 'branchId',
  group: 'groupId',
  member: 'memberId',
  'payment-order': 'orderId',
  event: 'eventId',
  attendee: 'attendeeId',
  ticket: 'ticketId',
  lead: 'leadId',
  user: 'userId',
  invitations: 'invitationId',
  'user-role': 'userOrgRoleId',
  expense: 'expenseId',
  trigger: 'webhookId',
  'org-app': 'apiId',
  'bank-account': 'bankId',
  'account-detail': 'bankId',
  document: 'documentId',
  'e-sign': 'documentId',
  job: 'jobId',
  report: 'jobId',
  provider: 'providerId',
  category: 'categoryId',
  source: 'sourceId',
  tag: 'tagId',
  status: 'statusId',
  note: 'noteId',
  task: 'taskId',
  call: 'callId',
  coupon: 'couponId',
  p: 'orderId'
};

/**
 * Fallback: known ID prefixes used by the backend.
 * Catches dynamic segments even when the preceding segment is not in the map.
 */
export const ID_PREFIX_MAP: [RegExp, string][] = [
  [/^org_/, ':organisationId'],
  [/^brch_/, ':branchId'],
  [/^grp_/, ':groupId'],
  [/^mem_/, ':memberId'],
  [/^ord_/, ':orderId']
];

/**
 * Converts a concrete API URL into a parameterized template.
 *
 * Example:
 *   "/v1/organisation/org_abc123/branch/brch_xyz?include=true"
 *   → "/v1/organisation/:organisationId/branch/:branchId"
 */
export const getUrlStruct = (url: string): string => {
  const pathOnly = url.split('?')[0].replace(/^\//, '');
  const segments = pathOnly.split('/');
  const result: string[] = [];
  let skipNext = false;

  for (let i = 0; i < segments.length; i += 1) {
    if (skipNext) {
      skipNext = false;
    } else {
      const segment = segments[i];
      const paramName = SEGMENT_PARAM_MAP[segment];

      if (paramName && i + 1 < segments.length) {
        result.push(segment, `:${paramName}`);
        skipNext = true;
      } else {
        const prefixMatch = ID_PREFIX_MAP.find(([re]) => re.test(segment));
        if (prefixMatch) {
          result.push(prefixMatch[1]);
        } else {
          result.push(segment);
        }
      }
    }
  }

  return `/${result.join('/')}`;
};
